"use client"

import { useState, useEffect } from "react"
import { Autocomplete, Box, InputAdornment, TextField, Typography, alpha, createFilterOptions } from "@mui/material"
import { Add as AddIcon, Business as BusinessIcon } from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import { useNotification } from "../../contexts/NotificationContext"
import { useOrganization } from "../../contexts/OrganizationContext"
import { getActiveOrganizations } from "../../services/organization.service"

const CREATE_NEW = { _id: "__create_new__", name: "Create New Organization" }

const filterByNameOrSlug = createFilterOptions({
  stringify: (organization) => `${organization.name} ${organization.slug || ""}`,
})

// "Create New Organization" stays at the bottom of the list even while searching
const filterOptions = (options, state) => [...filterByNameOrSlug(options, state), CREATE_NEW]

const OrganizationSelector = () => {
  const { user, selectedOrganization, selectOrganization } = useAuth()
  const { currentOrganization } = useOrganization()
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const [organizations, setOrganizations] = useState([])
  const [loading, setLoading] = useState(false)
  const isSuperAdmin = user?.role === "superadmin"

  useEffect(() => {
    if (!isSuperAdmin) return

    const fetchOrganizations = async () => {
      setLoading(true)
      try {
        const response = await getActiveOrganizations()
        setOrganizations(response.data)
      } catch (error) {
        showNotification(error.message || "Failed to load organizations", "error")
      } finally {
        setLoading(false)
      }
    }
    fetchOrganizations()
  }, [isSuperAdmin])

  const handleChange = (event, organization) => {
    if (organization?._id === CREATE_NEW._id) {
      navigate("/organizations/create")
    } else {
      selectOrganization(organization)
    }
  }

  if (!isSuperAdmin) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: 42,
          px: 2,
          borderRadius: 1,
          bgcolor: alpha("#000", 0.04),
        }}
      >
        <BusinessIcon sx={{ mr: 1, fontSize: 20, color: "primary.main" }} />
        <Typography variant="body2" fontWeight={500}>
          {currentOrganization?.name || "Your Organization"}
        </Typography>
      </Box>
    )
  }

  // Keep the selected organization listed even before the list loads, or if it is no longer active
  const options =
    selectedOrganization && !organizations.some((organization) => organization._id === selectedOrganization._id)
      ? [selectedOrganization, ...organizations]
      : organizations

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={options}
      loading={loading}
      value={selectedOrganization || null}
      onChange={handleChange}
      filterOptions={filterOptions}
      getOptionLabel={(organization) => organization.name}
      isOptionEqualToValue={(option, selected) => option._id === selected._id}
      clearText="Exit organization"
      noOptionsText="No organizations match"
      componentsProps={{ popper: { style: { width: 340 }, placement: "bottom-start" } }}
      openOnFocus
      autoHighlight
      renderOption={(props, organization) => (
        <li {...props} key={organization._id}>
          {organization._id === CREATE_NEW._id ? (
            <Box sx={{ display: "flex", alignItems: "center", color: "primary.main" }}>
              <AddIcon fontSize="small" sx={{ mr: 1 }} />
              {organization.name}
            </Box>
          ) : (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {organization.name}
              </Typography>
              {organization.slug && (
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {organization.slug}
                </Typography>
              )}
            </Box>
          )}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Select Organization"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <BusinessIcon sx={{ fontSize: 20, color: "primary.main" }} />
              </InputAdornment>
            ),
            sx: { borderRadius: 1, backgroundColor: alpha("#000", 0.04), fontSize: 14 },
          }}
        />
      )}
    />
  )
}

export default OrganizationSelector
